export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function initials(name: string, fallback = '?'): string {
  const result = name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return result || fallback;
}

export function initialsForUser(
  user?: { name?: string | null; full_name?: string | null; email?: string | null; email_address?: string | null } | null,
  fallback = '?',
): string {
  const name = user?.full_name ?? user?.name ?? null;
  const email = user?.email_address ?? user?.email ?? null;
  const source = name?.trim() || email?.trim() || '';
  if (!source) return fallback;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

// Normalize the second arg: callers historically pass a plain string fallback,
// but for event-local rendering we also need a timeZone hint. Accept either.
function normalizeOpts(opt?: string | { fallback?: string; timeZone?: string }): { fallback: string; timeZone?: string } {
  if (typeof opt === 'string') return { fallback: opt };
  return { fallback: opt?.fallback ?? '—', timeZone: opt?.timeZone };
}

export function formatDate(
  iso: string | null | undefined,
  opt?: string | { fallback?: string; timeZone?: string },
): string {
  const { fallback, timeZone } = normalizeOpts(opt);
  if (!iso) return fallback;
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', timeZone,
  });
}

// Event starts_at / ends_at are stored as UTC calendar dates (always 00:00 UTC),
// so the seminar date IS that UTC date. Render event dates with
// `{ timeZone: 'UTC' }` — NOT the event's timezone or browser-local, either of
// which shifts the date back a day for viewers/zones west of UTC.
// Omit timeZone only for system timestamps like created_at / received_at where
// browser-local is the right default.
export function formatDateTime(
  iso: string | null | undefined,
  opt?: string | { fallback?: string; timeZone?: string },
): string {
  const { fallback, timeZone } = normalizeOpts(opt);
  if (!iso) return fallback;
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone,
    // Include zone abbreviation (e.g., "CDT") so it's obvious the value is in
    // the event's TZ, not the viewer's. Skip when no timeZone is passed.
    timeZoneName: timeZone ? 'short' : undefined,
  });
}

// Format a DATE-ONLY value (YYYY-MM-DD — e.g. a Postgres `date` column) for
// display. Parses as LOCAL midnight so it never slips to the previous day the
// way formatDate does: `new Date("2026-07-01")` is UTC midnight, which renders
// as "Jun 30" in any negative-offset (US) timezone. Use this for date-only API
// fields; use formatDate for real timestamps (issued_at, paid_at, …).
export function formatDateOnly(ymd: string | null | undefined, fallback = '—'): string {
  if (!ymd) return fallback;
  return new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

// Today as a local YYYY-MM-DD string. Use for date-only string comparisons
// (e.g. "is this follow-up due?") so the boundary doesn't slip across UTC.
export function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
